'use client';

import type { EnglishIqResponseV2Type, IEnglishIqItemV2 } from '@repo/sdk';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { EnglishIqMessagesType } from '../../english-iq-messages-type';
import {
  EnglishIqV2ChoiceAnswer,
  EnglishIqV2ReorderAnswer,
  EnglishIqV2TypedAnswer,
} from './answer';
import { EnglishIqV2Progress } from './english-iq-v2-progress';
import { EnglishIqV2MediaPlayer } from './media';

interface IEnglishIqV2QuestionProps {
  answered: number;
  isBusy: boolean;
  item: IEnglishIqItemV2;
  messages: EnglishIqMessagesType['v2']['assessment'];
  onFinalAnswer?: () => void;
  onSubmit: (
    response: EnglishIqResponseV2Type,
    replayCount: number,
    responseTimeMs: number,
  ) => void;
  total: number;
}

export function EnglishIqV2Question({
  answered,
  isBusy,
  item,
  messages,
  onFinalAnswer,
  onSubmit,
  total,
}: IEnglishIqV2QuestionProps): React.JSX.Element {
  const headingRef = useRef<HTMLHeadingElement>(null);
  const startedAtRef = useRef(Date.now());
  const submittedRef = useRef(false);
  const [replayCount, setReplayCount] = useState(0);
  const submit = useCallback(
    (response: EnglishIqResponseV2Type) => {
      if (isBusy || submittedRef.current) {
        return;
      }
      submittedRef.current = true;
      if (answered === total - 1 && response.kind !== 'media_unavailable') {
        onFinalAnswer?.();
      }
      onSubmit(response, replayCount, Math.max(0, Date.now() - startedAtRef.current));
    },
    [answered, isBusy, onFinalAnswer, onSubmit, replayCount, total],
  );

  useEffect(() => {
    startedAtRef.current = Date.now();
    setReplayCount(0);
    headingRef.current?.focus();
  }, []);

  useEffect(() => {
    if (!('choices' in item)) {
      return;
    }
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.target instanceof HTMLInputElement || event.target instanceof HTMLTextAreaElement) {
        return;
      }
      const index = Number(event.key) - 1;
      const choice = item.choices[index];
      if (choice && index >= 0) {
        submit({ choiceId: choice.id, kind: 'choice' });
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [item, submit]);

  const progressLabel = messages.progressLabel
    .replace('{answered}', String(answered))
    .replace('{total}', String(total));
  return (
    <section className="mx-auto w-full max-w-4xl pb-[max(1rem,env(safe-area-inset-bottom))]">
      <EnglishIqV2Progress answered={answered} label={progressLabel} total={total} />
      <p className="mb-2 text-xs font-bold uppercase tracking-[0.2em] text-[#ff9b50]">
        {messages.progress
          .replace('{current}', String(answered + 1))
          .replace('{total}', String(total))}
      </p>
      <h2
        className="mb-6 max-w-3xl text-balance text-2xl font-semibold leading-tight text-[#fff8e9] outline-none sm:mb-8 sm:text-4xl"
        dir="ltr"
        ref={headingRef}
        tabIndex={-1}
      >
        {item.type === 'typed_cloze' ? item.sentence : item.prompt}
      </h2>
      {(item.type === 'audio_choice' || item.type === 'video_choice') && (
        <EnglishIqV2MediaPlayer
          item={item}
          mediaSkipLabel={messages.mediaSkip}
          onMediaUnavailable={(reason) => submit({ kind: 'media_unavailable', reason })}
          onReplayCountChange={setReplayCount}
          playLabel={messages.play}
          playsLeftLabel={messages.playsLeft}
        />
      )}
      <QuestionAnswer item={item} isBusy={isBusy} messages={messages} submit={submit} />
    </section>
  );
}

interface IQuestionAnswerProps {
  isBusy: boolean;
  item: IEnglishIqItemV2;
  messages: EnglishIqMessagesType['v2']['assessment'];
  submit: (response: EnglishIqResponseV2Type) => void;
}

function QuestionAnswer({
  item,
  isBusy,
  messages,
  submit,
}: IQuestionAnswerProps): React.JSX.Element {
  if (item.type === 'reorder') {
    return (
      <EnglishIqV2ReorderAnswer
        availableLabel={messages.availableTokens}
        clearLabel={messages.clear}
        disabled={isBusy}
        onSubmit={(tokenIds) => submit({ kind: 'reorder', tokenIds })}
        selectedLabel={messages.selectedTokens}
        submitLabel={messages.submit}
        tokens={item.tokens}
      />
    );
  }
  if (item.type === 'typed_cloze') {
    return (
      <EnglishIqV2TypedAnswer
        disabled={isBusy}
        onSubmit={(text) => submit({ kind: 'text', text })}
        placeholder={messages.typedHint}
        submitLabel={messages.submit}
      />
    );
  }
  return (
    <EnglishIqV2ChoiceAnswer
      choices={item.choices}
      disabled={isBusy}
      onSelect={(choiceId) => submit({ choiceId, kind: 'choice' })}
    />
  );
}
